import React from 'react';
import { toast } from 'react-hot-toast';
import { continueWithGoogle } from '../../services/authProviders';
import { syncProfileAfterAuthChange } from '../../services/authSession';
import { LINK_OUTCOME } from '../../utils/authIdentity';

/**
 * "Continue with Google".
 *
 * Basic profile sign-in only: YouTube scopes are requested separately, and only
 * if the host asks to import playlists.
 */
export default function GoogleAuthButton({
  label = 'Continue with Google',
  onSuccess,
  onSignedInInstead,
  redirectContext = null,
  className = '',
  disabled = false,
}) {
  const [inFlight, setInFlight] = React.useState(false);

  const handleClick = async () => {
    if (inFlight) return;
    setInFlight(true);

    try {
      const result = await continueWithGoogle({ context: redirectContext });

      switch (result.outcome) {
        case LINK_OUTCOME.linked:
          await syncProfileAfterAuthChange(result.user);
          toast.success('Google account connected');
          onSuccess?.(result);
          break;
        case LINK_OUTCOME.alreadyGoogle:
          await syncProfileAfterAuthChange(result.user);
          onSuccess?.(result);
          break;
        case LINK_OUTCOME.signedInInstead:
          await syncProfileAfterAuthChange(result.user);
          if (onSignedInInstead) {
            onSignedInInstead(result);
          } else {
            toast.success(`Signed in as ${result.user?.displayName || 'your Google account'}`);
            onSuccess?.(result);
          }
          break;
        case LINK_OUTCOME.redirectStarted:
          toast('Continuing with Google in this window…');
          break;
        case LINK_OUTCOME.cancelled:
          break;
        default:
          toast.error(result.error?.message || 'Google sign-in failed');
          break;
      }
    } finally {
      setInFlight(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || inFlight}
      className={`w-full flex items-center justify-center gap-3 py-3 px-4 rounded-lg bg-white text-gray-800 font-medium hover:bg-gray-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
    >
      <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.54 5.54 0 0 1-2.4 3.63v3.02h3.86c2.26-2.09 3.56-5.17 3.56-8.89z" />
        <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3.02c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z" />
        <path fill="#FBBC05" d="M5.27 14.27a7.2 7.2 0 0 1 0-4.54V6.64H1.29a11.99 11.99 0 0 0 0 10.72l3.98-3.09z" />
        <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.7 0 3.99 2.47 1.29 6.64l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
      </svg>
      <span>{inFlight ? 'Connecting…' : label}</span>
    </button>
  );
}
